import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Mail, User, Eye, EyeOff, Check, X, Shield } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { adminApi } from '@/lib/api';

// Password strength checker
const checkPasswordStrength = (password) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  
  const score = Object.values(checks).filter(Boolean).length;
  
  let strength = 'weak';
  let color = 'bg-red-500';
  
  if (score >= 4) {
    strength = 'strong';
    color = 'bg-green-500';
  } else if (score >= 3) {
    strength = 'medium';
    color = 'bg-yellow-500';
  }
  
  return { checks, score, strength, color };
};

const PasswordStrengthIndicator = ({ password }) => {
  const { checks, strength, color } = checkPasswordStrength(password);
  
  if (!password) return null;
  
  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i <= Object.values(checks).filter(Boolean).length ? color : 'bg-muted'
            }`}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Password strength: <span className={`font-medium ${strength === 'strong' ? 'text-green-600' : strength === 'medium' ? 'text-yellow-600' : 'text-red-600'}`}>{strength}</span>
      </p>
      <div className="grid grid-cols-2 gap-1 text-xs">
        <div className={`flex items-center gap-1 ${checks.length ? 'text-green-600' : 'text-muted-foreground'}`}>
          {checks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          8+ characters
        </div>
        <div className={`flex items-center gap-1 ${checks.uppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
          {checks.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          Uppercase
        </div>
        <div className={`flex items-center gap-1 ${checks.lowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
          {checks.lowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          Lowercase
        </div>
        <div className={`flex items-center gap-1 ${checks.number ? 'text-green-600' : 'text-muted-foreground'}`}>
          {checks.number ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          Number
        </div>
      </div>
    </div>
  );
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    name: '', 
    email: '', 
    password: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
    if (token) {
      // Set for API calls
      localStorage.setItem('admin_token', token);
      adminApi.verify()
        .then(() => navigate('/admin'))
        .catch(() => {
          localStorage.removeItem('admin_token');
          sessionStorage.removeItem('admin_token');
        });
    }
    
    // Load remembered email
    const rememberedEmail = localStorage.getItem('admin_remembered_email');
    if (rememberedEmail) {
      setLoginData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, [navigate]);

  const validateLogin = () => {
    const newErrors = {};
    if (!loginData.email) newErrors.loginEmail = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(loginData.email)) newErrors.loginEmail = 'Invalid email format';
    if (!loginData.password) newErrors.loginPassword = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRegister = () => {
    const newErrors = {};
    if (!registerData.name) newErrors.registerName = 'Name is required';
    if (!registerData.email) newErrors.registerEmail = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(registerData.email)) newErrors.registerEmail = 'Invalid email format';
    if (!registerData.password) newErrors.registerPassword = 'Password is required';
    else if (registerData.password.length < 8) newErrors.registerPassword = 'Password must be at least 8 characters';
    if (registerData.password !== registerData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!acceptTerms) newErrors.terms = 'You must accept the terms';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;

    setLoading(true);
    setErrors({});
    
    try {
      const res = await adminApi.login(loginData);
      const token = res.data.token;
      
      // Store token based on remember me
      if (rememberMe) {
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_remembered_email', loginData.email);
      } else {
        sessionStorage.setItem('admin_token', token);
        localStorage.setItem('admin_token', token); // Still needed for API calls
        localStorage.removeItem('admin_remembered_email');
      }
      
      toast.success('Login successful!');
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid credentials';
      setErrors({ loginGeneral: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateRegister()) return;

    setLoading(true);
    setErrors({});
    
    try {
      const res = await adminApi.register({
        name: registerData.name,
        email: registerData.email,
        password: registerData.password
      });
      
      localStorage.setItem('admin_token', res.data.token);
      toast.success('Welcome to SEG Career Support CMS!', {
        description: 'Your admin account has been created successfully.'
      });
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      setErrors({ registerGeneral: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md" data-testid="admin-login-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-2xl font-heading">Admin Portal</CardTitle>
          <CardDescription>SEG Career Support CMS</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            {/* LOGIN TAB */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                {errors.loginGeneral && (
                  <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm" data-testid="login-error">
                    {errors.loginGeneral}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="admin@example.com"
                      className={`pl-10 ${errors.loginEmail ? 'border-destructive' : ''}`}
                      value={loginData.email}
                      onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                      data-testid="login-email-input"
                    />
                  </div>
                  {errors.loginEmail && <p className="text-xs text-destructive">{errors.loginEmail}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type={showLoginPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className={`pl-10 pr-10 ${errors.loginPassword ? 'border-destructive' : ''}`}
                      value={loginData.password}
                      onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      data-testid="login-toggle-password"
                    >
                      {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.loginPassword && <p className="text-xs text-destructive">{errors.loginPassword}</p>}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="remember-me" 
                      checked={rememberMe}
                      onCheckedChange={setRememberMe}
                      data-testid="remember-me-checkbox"
                    />
                    <Label htmlFor="remember-me" className="text-sm font-normal cursor-pointer">
                      Remember me
                    </Label>
                  </div>
                  <Link 
                    to="/admin/forgot-password" 
                    className="text-sm text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>

            {/* REGISTER TAB */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {errors.registerGeneral && (
                  <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm" data-testid="register-error">
                    {errors.registerGeneral}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="register-name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="register-name"
                      placeholder="Your name"
                      className={`pl-10 ${errors.registerName ? 'border-destructive' : ''}`}
                      value={registerData.name}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, name: e.target.value }))}
                      data-testid="register-name-input"
                    />
                  </div>
                  {errors.registerName && <p className="text-xs text-destructive">{errors.registerName}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="register-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="admin@example.com"
                      className={`pl-10 ${errors.registerEmail ? 'border-destructive' : ''}`}
                      value={registerData.email}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                      data-testid="register-email-input"
                    />
                  </div>
                  {errors.registerEmail && <p className="text-xs text-destructive">{errors.registerEmail}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="register-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="register-password"
                      type={showRegisterPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className={`pl-10 pr-10 ${errors.registerPassword ? 'border-destructive' : ''}`}
                      value={registerData.password}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      data-testid="register-toggle-password"
                    >
                      {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.registerPassword && <p className="text-xs text-destructive">{errors.registerPassword}</p>}
                  <PasswordStrengthIndicator password={registerData.password} />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="register-confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="register-confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className={`pl-10 pr-10 ${errors.confirmPassword ? 'border-destructive' : ''}`}
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      data-testid="register-confirm-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      data-testid="register-toggle-confirm-password"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword}</p>}
                  {registerData.confirmPassword && registerData.password === registerData.confirmPassword && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Passwords match
                    </p>
                  )}
                </div>
                
                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="accept-terms" 
                    checked={acceptTerms}
                    onCheckedChange={setAcceptTerms}
                    className={errors.terms ? 'border-destructive' : ''}
                    data-testid="accept-terms-checkbox"
                  />
                  <Label htmlFor="accept-terms" className="text-sm font-normal leading-tight cursor-pointer">
                    I agree to the{' '}
                    <a href="/terms" className="text-primary hover:underline" target="_blank">
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a href="/privacy" className="text-primary hover:underline" target="_blank">
                      Privacy Policy
                    </a>
                  </Label>
                </div>
                {errors.terms && <p className="text-xs text-destructive">{errors.terms}</p>}
                
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
